import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import _ from 'lodash';
import countries from '../common/countries';
import { routes } from '../../redux/api/routes';
import { ajaxRequestCompleted, ajaxRequestInitiated, genericB2BAccountRequest, getAccountDetails, getApprovedAccounts, siteManagement } from '../../redux/actions';
import { additionalAddressValidationErrorCodesForDpm, addressValidationErrorCode } from '../../redux/util/addressValidationErrorCodes';
import AddressVerificationForm from '../common/addressVerificationForm';
import { sendRequest } from '../../redux/api/sendRequest';

const INITIAL_FORM_DATA = {
	emailId: '',
	companyName: '',
	taxId: '',
	propertyName: '',
	shippingAddress: {
		addressLine1: '',
		addressLine2: '',
		city: '',
		state: '',
		postalCode: ''
	},
	billingAddress: {
		addressLine1: '',
		addressLine2: '',
		city: '',
		state: '',
		postalCode: ''
	},
	phoneNumber: ''
};

const US_CONTIGUOUS_STATES = countries.find(country => country.code === 'US').regions.filter(region => !['AK', 'HI'].includes(region.code));

const ADDRESS_VALIDATION_ERROR_CODES = { ...addressValidationErrorCode, ...additionalAddressValidationErrorCodesForDpm };
const SHIPPING_ADDRESS_VALIDATION_ERRORS = Object.entries(ADDRESS_VALIDATION_ERROR_CODES)
	.filter(entry => !['AE08', 'AE09', 'AE17'].includes(entry[0]))
	.map(error => ({ code: error[0], message: error[1] }));
const BILLING_ADDRESS_VALIDATION_ERRORS = Object.entries(ADDRESS_VALIDATION_ERROR_CODES)
	.filter(entry => !['AE17'].includes(entry[0]))
	.map(error => ({ code: error[0], message: error[1] }));
const ALLOWED_BILLING_ADDRESS_VALIDATION_ERROR_CODES = ['AE08', 'AE09'];

const convertMuleToSuggestedAddress = address => ({
	addressline1: address.ADDRESS_LINE1,
	addressline2: address.ADDRESS_LINE2,
	city: address.CITY,
	county: address.COUNTY,
	state: address.STATE,
	country: address.COUNTRY,
	postalcode: address.POSTALCODE
});

const AddNewPropertyDialog = props => {
	const [currentScreen, setCurrentScreen] = useState('FORM'); // FORM, SHIP_ADDR_VAL, BILL_ADDR_VAL, TAX_VAL, TDONE
	const [isBillingAddressSameAsShippingAddress, setIsBillingAddressSameAsShippingAddress] = useState(false);
	const [acknowledgement, setAcknowledgement] = useState();
	const shippingAddressRef = useRef({});
	const billingAddressRef = useRef({});
	const dialogWindowRef = useRef();
	const [dialogCoordinates, setDialogCoordinates] = useState({
		x: 0,
		y: 0
	});
	const [canDrag, setCanDrag] = useState(false);
	const canDragRef = useRef(canDrag);
	canDragRef.current = canDrag;
	const { user } = useSelector(({ user }) => ({ user }));
	const [formData, setFormData] = useState(() => {
		const accountDetails = (user.accountDetail && user.accountDetail.responseObject && user.accountDetail.responseObject[0] && user.accountDetail.responseObject[0]) || {};
		return {
			...INITIAL_FORM_DATA,
			userId: user.userDetails.sso,
			firstName: user.userDetails.firstName,
			lastName: user.userDetails.lastName,
			emailId: user.userDetails.email,
			companyName: accountDetails.ORGANIZATIONNAME || ''
		};
	});
	const [isValidatingTaxId, setIsValidatingTaxId] = useState(false);
	const payloadRef = useRef();
	payloadRef.current = JSON.parse(JSON.stringify(formData));
	const addPropertyResponseRef = useRef();
	const taxIdValidationRef = useRef({
		isCustomerAccount: false
	});

	const [errors, setErrors] = useState({});
	const dispatch = useDispatch();

	useLayoutEffect(() => {
		setDialogCoordinates({
			x: window.innerWidth / 2 - dialogWindowRef.current.offsetWidth / 2,
			y: (window.innerHeight * 15) / 100
		});
		const handleDrag = e => {
			const offsetX = e.movementX;
			const offsetY = e.movementY;
			if (canDragRef.current) {
				setDialogCoordinates(prevDialogCoordinates => ({
					x: Math.min(Math.max(prevDialogCoordinates.x + offsetX, 0), window.innerWidth - dialogWindowRef.current.offsetWidth / 2),
					y: Math.min(Math.max(prevDialogCoordinates.y + offsetY, 0), window.innerHeight - dialogWindowRef.current.offsetHeight / 2)
				}));
			}
		};
		const handleDisableDrag = () => {
			setCanDrag(false);
		};
		const handleWindowResize = () => {
			setDialogCoordinates(prevDialogCoordinates => ({ ...prevDialogCoordinates, x: window.innerWidth / 2 - dialogWindowRef.current.offsetWidth / 2 }));
		};
		window.addEventListener('mousemove', handleDrag);
		window.addEventListener('mouseup', handleDisableDrag);
		window.addEventListener('blur', handleDisableDrag);
		window.addEventListener('resize', handleWindowResize);
		return () => {
			window.removeEventListener('mousemove', handleDrag);
			window.removeEventListener('mouseup', handleDisableDrag);
			window.removeEventListener('blur', handleDisableDrag);
			window.removeEventListener('resize', handleWindowResize);
		};
	}, []);

	useEffect(() => {
		dispatch(
			genericB2BAccountRequest(
				`requestType=getTaxIDByAccount&accountNumber=${user.accountDetail && user.accountDetail.responseObject && user.accountDetail.responseObject[0].ACCOUNTNUMBER}`,
				null,
				true,
				false,
				taxIdResponse => {
					if (taxIdResponse.responseObject && taxIdResponse.responseObject[0] && taxIdResponse.responseObject[0].JGZZ_FISCAL_CODE) {
						taxIdValidationRef.current.isCustomerAccount = true;
						setFormData(prevFormData => ({
							...prevFormData,
							taxId: taxIdResponse.responseObject[0].JGZZ_FISCAL_CODE || ''
						}));
					}
				}
			)
		);
	}, []);

	useEffect(() => {
		if (isBillingAddressSameAsShippingAddress) {
			setFormData(prevFormData => ({
				...prevFormData,
				billingAddress: {
					...prevFormData.shippingAddress
				}
			}));
			setErrors(prevErrors => {
				const updatedErrors = { ...prevErrors };
				let anyShippingAddressError = false;
				for (const key in updatedErrors) {
					if (key.startsWith('shippingAddress.')) {
						anyShippingAddressError = true;
						updatedErrors[`billingAddress.${key.split('.').at(-1)}`] = updatedErrors[key];
					}
				}
				if (!anyShippingAddressError) {
					for (const key in updatedErrors) {
						if (key.startsWith('billingAddress.')) {
							updatedErrors[key] = '';
						}
					}
				}
				return updatedErrors;
			});
		}
	}, [isBillingAddressSameAsShippingAddress]);

	useEffect(() => {
		if (payloadRef.current && addPropertyResponseRef.current) {
			if (payloadRef.current.taxId !== addPropertyResponseRef.current.taxId) {
				addPropertyResponseRef.current = {
					taxId: '',
					userIdTaxId: '',
					accountNumber: ''
				};
			}
		}
	}, [payloadRef.current, addPropertyResponseRef.current]);

	const getNestedValue = name => {
		if (name.includes('.')) {
			const [parentKey, childKey] = name.split('.');
			return formData[parentKey][childKey];
		}
		return formData[name];
	};

	const validateField = (fieldName, fieldValue) => {
		fieldValue = fieldValue.trim();
		let errorMessage = '';
		if (['shippingAddress.addressLine2', 'billingAddress.addressLine2', 'shippingAddress.county', 'billingAddress.county'].includes(fieldName)) {
			return '';
		}
		if (!fieldValue) {
			return `${_.startCase(fieldName.split('.').at(-1))} is required`;
		}
		switch (fieldName) {
			case 'emailId':
				if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(fieldValue)) {
					errorMessage = 'Invalid Email';
				}
				break;
			case 'taxId':
				if (!/^[0-9]{9}$/.test(fieldValue)) {
					errorMessage = 'Tax ID should be numeric and exactly 9 digits';
				}
				break;
			case 'phoneNumber':
				if (!/^(?:\d{10}|\(\d{3}\)\s\d{3}-\d{4})$/.test(fieldValue)) {
					errorMessage = 'Please enter a valid US Phone Number';
				}
				break;
			case 'shippingAddress.postalCode':
			case 'billingAddress.postalCode':
				if (!/^\d{5}(?:[-\s]\d{4})?$/.test(fieldValue)) {
					errorMessage = 'Please enter a valid US Zip Code';
				}
				break;
		}
		return errorMessage;
	};

	const handleChange = e => {
		const { name, value } = e.target;
		if (name === 'taxId') {
			taxIdValidationRef.current.isCustomerAccount = false;
		}

		if (name.includes('.')) {
			const [parentKey, childKey] = name.split('.');
			setFormData(prevData => {
				const updatedFormData = {
					...prevData,
					[parentKey]: {
						...prevData[parentKey],
						[childKey]: value
					}
				};
				if (isBillingAddressSameAsShippingAddress) {
					updatedFormData.billingAddress = {
						...prevData[parentKey],
						[childKey]: value
					};
				}

				return updatedFormData;
			});
		} else {
			setFormData(prevData => ({
				...prevData,
				[name]: value
			}));
		}

		const errorMessage = validateField(name, value);
		if (errors[name] && !errorMessage) {
			setErrors(prevErrors => {
				const updatedErrors = {
					...prevErrors,
					[name]: ''
				};
				if (isBillingAddressSameAsShippingAddress && name.startsWith('shippingAddress.')) {
					updatedErrors[`billingAddress.${name.split('.').at(-1)}`] = '';
				}
				return updatedErrors;
			});
		}
	};

	const isTaxIdLinkedToCustomerAccount = taxValidationResponse => {
		const approvedAccounts = user.approvedAccounts && user.approvedAccounts.responseObject;
		return (
			taxValidationResponse.responseMessage &&
			approvedAccounts &&
			approvedAccounts.length &&
			approvedAccounts.some(approvedAccount => taxValidationResponse.responseMessage.endsWith(`Account Number ${approvedAccount.accountNumber}`))
		);
	};

	const handleBlur = e => {
		const { name, value } = e.target;

		const errorMessage = validateField(name, value);
		if (errorMessage) {
			setErrors(prevErrors => {
				const updatedErrors = {
					...prevErrors,
					[name]: errorMessage
				};
				if (isBillingAddressSameAsShippingAddress && name.startsWith('shippingAddress.')) {
					updatedErrors[`billingAddress.${name.split('.').at(-1)}`] = errorMessage;
				}
				return updatedErrors;
			});
		}
		if (name === 'taxId' && !taxIdValidationRef.current.isCustomerAccount && !errorMessage) {
			taxIdValidationRef.current.isCustomerAccount = false;

			taxIdValidationRef.current.inactiveAccounts = [];
			taxIdValidationRef.current.isBlocked = false;
			setIsValidatingTaxId(true);
			dispatch(
				genericB2BAccountRequest({ taxId: value }, 'requestType=taxValidation', false, false, taxValidationResponse => {
					const taxIdAccounts = taxValidationResponse.responseObject ? JSON.parse(taxValidationResponse.responseObject) : [];
					if (taxIdAccounts.length) {
						const inactiveTaxIdAccounts = taxIdAccounts.filter(taxIdAccount => taxIdAccount.STATUS === 'I').map(taxIdAccount => taxIdAccount.ACCOUNT_NUMBER);
						if (inactiveTaxIdAccounts.length === taxIdAccounts.length) {
							taxIdValidationRef.current.inactiveAccounts = inactiveTaxIdAccounts;
							setIsValidatingTaxId(false);
						} else {
							const { userOtherDetails } = user;
							const personPartyId =
								userOtherDetails && userOtherDetails.responseObject && userOtherDetails.responseObject[0] && userOtherDetails.responseObject[0].PERSON_PARTY_ID;
							dispatch(
								getApprovedAccounts(
									`personPartyId=${personPartyId}&requestType=getUserAccounts`,
									response => {
										const approvedAccounts = response.responseObject && response.responseObject.length ? response.responseObject : [];

										// If any of the accounts tied to this user is already linked to the taxId, allow adding new property,
										// else if the tax id is linked to any other active account do not allow
										if (
											taxValidationResponse.responseMessage &&
											approvedAccounts &&
											approvedAccounts.length &&
											approvedAccounts.some(approvedAccount =>
												taxIdAccounts.some(taxIdAccount => taxIdAccount.ACCOUNT_NUMBER === approvedAccount.ACCOUNTNUMBER)
											)
										) {
											taxIdValidationRef.current.isCustomerAccount = true;
											setIsValidatingTaxId(false);
										} else {
											if (taxIdAccounts.some(taxIdAccount => taxIdAccount.STATUS === 'A')) {
												setAcknowledgement({
													title: 'Tax ID exists',
													content: 'An account already exists for this Tax ID. Please request access to that existing account'
												});
												setCurrentScreen('DONE');
											}
											setIsValidatingTaxId(false);
										}
									},
									false
								)
							);
						}
					} else {
						setIsValidatingTaxId(false);
					}
				})
			);
			dispatch(
				siteManagement({}, `requestType=getEntity&taxId=${value}`, false, entityResponse => {
					if (entityResponse.responseObject && entityResponse.responseObject[0] && entityResponse.responseObject[0].status === 'BLOCKED') {
						taxIdValidationRef.current.isBlocked = true;
					}
				})
			);
		}
	};

	const getTargetsForAddressErrorCode = (addressType, errorCode) => {
		switch (errorCode) {
			case 'AE01':
				return [`${addressType}.postalCode`];
			case 'AE07':
				return [`${addressType}.addressLine1`, `${addressType}.city`, `${addressType}.state`, `${addressType}.postalCode`];
			default:
				return [`${addressType}.addressLine1`];
		}
	};

	const validateAddressResponse = (addressType, addressResponse) => {
		const addressValidationErrorsWithTargets = [];
		const melissaAddressValidationErrors = addressType === 'shippingAddress' ? SHIPPING_ADDRESS_VALIDATION_ERRORS : BILLING_ADDRESS_VALIDATION_ERRORS;
		const melissaAddressValidationErrorCodesFromApi = addressResponse.RESULTS.split(',');

		melissaAddressValidationErrors.forEach(melissaAddressValidationError => {
			const isAnErrorCode = melissaAddressValidationErrorCodesFromApi.some(
				melissaAddressValidationErrorCodeFromApi => melissaAddressValidationErrorCodeFromApi === melissaAddressValidationError.code
			);

			if (isAnErrorCode) {
				addressValidationErrorsWithTargets.push({
					...melissaAddressValidationError,
					targets: getTargetsForAddressErrorCode(addressType, melissaAddressValidationError.code)
				});
			}
		});

		return addressValidationErrorsWithTargets;
	};

	const validateFormData = () => {
		const errors = {};

		const validate = (data, parentFieldName) => {
			for (const fieldName in data) {
				const fieldNamePath = `${parentFieldName ? parentFieldName + '.' : ''}${fieldName}`;
				if (typeof data[fieldName] === 'object') {
					validate(data[fieldName], fieldNamePath);
				} else {
					const errorMessage = validateField(fieldNamePath, data[fieldName]);
					if (errorMessage) {
						errors[fieldNamePath] = errorMessage;
					}
				}
			}
		};

		validate(formData);
		return errors;
	};

	const notifyRisk = () => {
		const now = new Date();
		dispatch(
			genericB2BAccountRequest(
				{
					templateName: 'RISK_REVIEW_TEMPLATE',
					userId: formData.userId,
					emailTo: 'cfsriskmanagement@geappliances.com',
					requestNumber: `${formData.userId}${formData.taxId}_` + `${now.getDate().toString().padStart(2, '0')}` + `${(now.getMonth() + 1).toString().padStart(2, '0')}`
				},
				`requestType=sendEmail`,
				false,
				false
			)
		);
	};

	const updateRequestStatus = (taxIdStatus, userAccount, callback) => {
		const payload = { ...userAccount };
		payload.errorMessage = (payload.errorMessage || '').replaceAll('Tax validation is failed;', '');
		payload.status = 'PENDING';
		if (taxIdStatus === 'INACTIVE') {
			payload.errorMessage = `TAX ID on inactive account ${taxIdValidationRef.current.inactiveAccounts.join(',')};` + payload.errorMessage;
		} else if (taxIdStatus === 'BLOCKED') {
			payload.errorMessage = 'Blocked Tax ID - New Property;';
		}
		dispatch(
			genericB2BAccountRequest(payload, `requestType=updateAccountStatus`, true, false, () => {
				callback && callback();
			})
		);
	};

	const handleCreateAccount = () => {
		const payload = JSON.parse(JSON.stringify(payloadRef.current));
		payload.shippingAddress.zipCode = payload.shippingAddress.postalCode;
		payload.billingAddress.zipCode = payload.billingAddress.postalCode;
		payload.userIdTaxId = addPropertyResponseRef.current && addPropertyResponseRef.current.userIdTaxId;
		payload.accountNumber = addPropertyResponseRef.current && addPropertyResponseRef.current.accountNumber;
		dispatch(
			genericB2BAccountRequest(payload, 'requestType=addUserAccount', true, false, userAccountResponse => {
				const userAccountResponseData = userAccountResponse.responseObject;

				if (userAccountResponseData) {
					addPropertyResponseRef.current = {
						taxId: userAccountResponseData.userAccount && userAccountResponseData.userAccount.taxId,
						userIdTaxId: userAccountResponseData.userAccount && userAccountResponseData.userAccount.userIdTaxId,
						accountNumber: userAccountResponseData.userAccount && userAccountResponseData.userAccount.accountNumber
					};
					if (userAccountResponseData.userAccount.attemptCount <= 3) {
						if (taxIdValidationRef.current.isBlocked) {
							updateRequestStatus('BLOCKED', userAccountResponseData.userAccount, () => {
								setAcknowledgement({
									title: 'Request Submitted',
									content: "Your request has been submitted successfully and is now being reviewed by our team. We'll keep you posted as soon as it's processed."
								});
								setCurrentScreen('DONE');
								notifyRisk();
							});
						} else if (
							(userAccountResponseData.taxResponse.status ||
								(userAccountResponseData.taxResponse.message.toLowerCase() === 'tax id exists' && taxIdValidationRef.current.isCustomerAccount)) &&
							userAccountResponseData.shippingAddressResponse.isValid &&
							userAccountResponseData.billingAddressResponse.isValid
						) {
							dispatch(
								genericB2BAccountRequest(userAccountResponseData.userAccount, 'requestType=approveUserAccount', true, false, approvalResponse => {
									const approvalData = approvalResponse.responseObject;
									if (approvalResponse.status && approvalData) {
										dispatch(genericB2BAccountRequest(approvalData, `requestType=consent`, false, false));
										dispatch(genericB2BAccountRequest(approvalData, `requestType=updateAccount`, false, false));
										setAcknowledgement({
											title: 'Approved',
											content: (
												<p>
													Account - {approvalData.customerAccountResponse && approvalData.customerAccountResponse.X_ACCOUNT_NUMBER} has been created
													successfully, you will receive an email once the account has been linked to your user ID and is ready for ordering.
													<br />
													<br />
													Once email is received, please log out and log back in to see this account added to your drop down.
												</p>
											)
										});
										setCurrentScreen('DONE');
									} else {
										setAcknowledgement({
											title: 'Error',
											content: `Something went wrong`
										});
										setCurrentScreen('DONE');
									}
								})
							);
						} else {
							if (
								userAccountResponseData.userAccount.attemptCount === 3 &&
								((!userAccountResponseData.taxResponse.status && !userAccountResponseData.taxResponse.message.toLowerCase().includes('inactive tax id')) ||
									!userAccountResponseData.shippingAddressResponse.isValid ||
									!userAccountResponseData.billingAddressResponse.isValid)
							) {
								if (!userAccountResponseData.taxResponse.status && !userAccountResponseData.taxResponse.message.toLowerCase().includes('inactive tax id')) {
									setAcknowledgement({
										title: 'Request Submitted',
										content: (
											<p>
												This Tax ID has been flagged and is under further review. If more information is required, we will communicate via{' '}
												<strong>{formData.emailId}</strong>
											</p>
										)
									});
									setCurrentScreen('DONE');
									notifyRisk();
								} else {
									setAcknowledgement({
										title: 'Request Submitted',
										content: (
											<p>
												Address validation invalid, information has been sent for further review. You will be contacted at{' '}
												<strong>{formData.emailId}</strong>
											</p>
										)
									});
									setCurrentScreen('DONE');
									notifyRisk();
								}
							} else if (
								!userAccountResponseData.taxResponse.status &&
								userAccountResponseData.taxResponse.message.toLowerCase().includes('inactive tax id') &&
								userAccountResponseData.shippingAddressResponse.isValid &&
								userAccountResponseData.billingAddressResponse.isValid
							) {
								updateRequestStatus('INACTIVE', userAccountResponseData.userAccount, () => {
									setAcknowledgement({
										title: 'Request Submitted',
										content:
											"Your request has been submitted successfully and is now being reviewed by our team. We'll keep you posted as soon as it's processed."
									});
									setCurrentScreen('DONE');
									notifyRisk();
								});
							} else if (!userAccountResponseData.taxResponse.status && !userAccountResponseData.taxResponse.message.toLowerCase().includes('inactive tax id')) {
								taxIdValidationRef.current.isError = true;
								setCurrentScreen('TAX_VAL');
							} else {
								handleShippingAddressVerificationModal();
							}
						}
					} else {
						setAcknowledgement({
							title: 'Limit Exceeded',
							content: (
								<p>
									This request is already under review. If more information is required, we will communicate via <strong>{formData.emailId}</strong>
								</p>
							)
						});
						setCurrentScreen('DONE');
					}
				}
			})
		);
	};

	const populateSelectedAddress = addressType => {
		const addressRef = addressType === 'shippingAddress' || isBillingAddressSameAsShippingAddress ? shippingAddressRef : billingAddressRef;
		const selectedAddressType = addressRef.current.selected;
		if (selectedAddressType && addressRef.current[selectedAddressType]) {
			const updatedAddress =
				selectedAddressType === 'validated'
					? {
							addressLine1: addressRef.current[selectedAddressType].responseObject.addressline1,
							addressLine2: addressRef.current[selectedAddressType].responseObject.addressline2,
							city: addressRef.current[selectedAddressType].responseObject.city,
							county: addressRef.current[selectedAddressType].responseObject.county,
							state: addressRef.current[selectedAddressType].responseObject.state,
							postalCode: addressRef.current[selectedAddressType].responseObject.postalcode
					  }
					: {
							addressLine1: addressRef.current[selectedAddressType].addressLine1,
							addressLine2: addressRef.current[selectedAddressType].addressLine2,
							city: addressRef.current[selectedAddressType].city,
							county: addressRef.current[selectedAddressType].county,
							state: addressRef.current[selectedAddressType].state,
							postalCode: addressRef.current[selectedAddressType].postalCode
					  };
			payloadRef.current = {
				...payloadRef.current,
				[addressType]: {
					...payloadRef.current[addressType],
					...updatedAddress
				}
			};
			setFormData(prevFormData => ({
				...prevFormData,
				[addressType]: {
					...prevFormData[addressType],
					...updatedAddress
				}
			}));
		}
	};

	const isPoOrHcOrPmbAddress = address => {
		const poOrHcBoxRegex = /\b(?:P(\.?O\.?|OST)\s*BOX|HC\s*\d+\s*BOX\s*\d+|PMB\s*\d+)\b/i;
		return poOrHcBoxRegex.test(address.addressLine1) || poOrHcBoxRegex.test(address.addressLine2 || '');
	};

	const handleBillingAddressVerificationModal = () => {
		if (billingAddressRef.current.entered && billingAddressRef.current.validated) {
			let errorMessage;
			const messages = [];
			if (billingAddressRef.current.errors && billingAddressRef.current.errors.length) {
				if (!errorMessage) {
					errorMessage = 'This is an invalid address';
				}
				billingAddressRef.current.errors.forEach(error => {
					if (!messages.includes(error.message)) {
						messages.push(error.message);
					}
				});
			}

			billingAddressRef.current.cancelLabel = 'Back';
			billingAddressRef.current.confirmLabel = taxIdValidationRef.current.isError ? 'OK' : 'Submit';
			billingAddressRef.current.errorMessage = errorMessage;
			billingAddressRef.current.messages = messages;
			setCurrentScreen('BILL_ADDR_VAL');
		}
	};

	const handleConfirmBillingAddress = () => {
		if (taxIdValidationRef.current.isError) {
			setCurrentScreen('FORM');
		} else {
			handleCreateAccount();
		}
	};

	const handleConfirmShippingAddress = () => {
		if (shippingAddressRef.current.selected) {
			if (isBillingAddressSameAsShippingAddress) {
				if (taxIdValidationRef.current.isError) {
					setCurrentScreen('FORM');
				} else {
					handleCreateAccount();
				}
			} else {
				if (shippingAddressRef.current.selected) {
					handleBillingAddressVerificationModal();
				}
			}
		}
	};

	const handleShippingAddressVerificationModal = () => {
		if (shippingAddressRef.current.entered && shippingAddressRef.current.validated) {
			let errorMessage;
			const messages = [];
			if (isPoOrHcOrPmbAddress(shippingAddressRef.current.entered)) {
				errorMessage = 'A PO/HC Box cannot be used as a Shipping Address, please provide a valid Shipping Address';
			} else {
				if (shippingAddressRef.current.errors && shippingAddressRef.current.errors.length) {
					if (!errorMessage) {
						errorMessage = 'This is an invalid address';
					}
					shippingAddressRef.current.errors.forEach(error => {
						if (!messages.includes(error.message)) {
							messages.push(error.message);
						}
					});
				}
				if (isBillingAddressSameAsShippingAddress && billingAddressRef.current.errors && billingAddressRef.current.errors.length) {
					if (!errorMessage) {
						errorMessage = 'This is an invalid address';
					}
					billingAddressRef.current.errors.forEach(error => {
						if (!messages.includes(error.message)) {
							messages.push(error.message);
						}
					});
				}
			}

			shippingAddressRef.current.cancelLabel = 'Back';
			shippingAddressRef.current.confirmLabel = isBillingAddressSameAsShippingAddress ? (taxIdValidationRef.current.isError ? 'OK' : 'Submit') : 'Verify Billing Address';
			shippingAddressRef.current.errorMessage = errorMessage;
			shippingAddressRef.current.messages = messages;
			setCurrentScreen('SHIP_ADDR_VAL');
		}
	};

	const handleCloseTaxIdVerificationModal = () => {
		if (shippingAddressRef.current.errors && shippingAddressRef.current.errors.length) {
			handleShippingAddressVerificationModal();
		} else if (billingAddressRef.current.errors && billingAddressRef.current.errors.length) {
			handleBillingAddressVerificationModal();
		} else {
			setCurrentScreen('FORM');
		}
	};

	const handleValidation = async e => {
		e.preventDefault();
		for (const key in errors) {
			if (errors[key]) {
				return;
			}
		}
		const newErrors = validateFormData();
		if (Object.keys(newErrors).length > 0) {
			setErrors(newErrors);
			return;
		}

		taxIdValidationRef.current.isError = false;
		dispatch(ajaxRequestInitiated());
		const shippingAddressValidationPromise = sendRequest(
			routes.b2bPortalAPIUrl,
			routes.users.b2bAccounts.path,
			'',
			'GET',
			`requestType=validateAddress&addressLine1=${formData.shippingAddress.addressLine1}&addressLine2=${formData.shippingAddress.addressLine2}&city=${formData.shippingAddress.city}&state=${formData.shippingAddress.state}&zip=${formData.shippingAddress.zipCode}&country=US`
		);
		const billingAddressValidationPromise = sendRequest(
			routes.b2bPortalAPIUrl,
			routes.users.b2bAccounts.path,
			'',
			'GET',
			`requestType=validateAddress&addressLine1=${formData.billingAddress.addressLine1}&addressLine2=${formData.billingAddress.addressLine2}&city=${formData.billingAddress.city}&state=${formData.billingAddress.state}&zip=${formData.billingAddress.zipCode}&country=US`
		);

		Promise.all([shippingAddressValidationPromise, billingAddressValidationPromise])
			.then(([shippingAddressValidationResponseData, billingAddressValidationResponseData]) => {
				if (shippingAddressValidationResponseData.responseObject && billingAddressValidationResponseData.responseObject) {
					shippingAddressRef.current.errors = validateAddressResponse('shippingAddress', shippingAddressValidationResponseData.responseObject);
					billingAddressRef.current.errors = validateAddressResponse('billingAddress', billingAddressValidationResponseData.responseObject);

					shippingAddressRef.current.entered = {
						...formData.shippingAddress
					};
					shippingAddressRef.current.validated = {
						responseObject: convertMuleToSuggestedAddress(shippingAddressValidationResponseData.responseObject)
					};
					shippingAddressRef.current.selected = 'validated';
					populateSelectedAddress('shippingAddress');

					billingAddressRef.current.entered = {
						...formData.billingAddress
					};
					billingAddressRef.current.validated = {
						responseObject: convertMuleToSuggestedAddress(billingAddressValidationResponseData.responseObject)
					};
					billingAddressRef.current.selected = 'validated';
					populateSelectedAddress('billingAddress');

					if (isPoOrHcOrPmbAddress(shippingAddressRef.current.entered)) {
						handleShippingAddressVerificationModal();
					} else if (shippingAddressRef.current.errors.length || billingAddressRef.current.errors.length) {
						handleCreateAccount();
					} else {
						handleShippingAddressVerificationModal();
					}
				}
			})
			.finally(() => {
				dispatch(ajaxRequestCompleted());
			});
	};

	const handleClose = () => {
		props.handleDialog('AddNewPropertyDialog', false);
	};

	const toggleIsBillingAddressSameAsShippingAddress = e => {
		setIsBillingAddressSameAsShippingAddress(isBillingAddressSameAsShippingAddress => !isBillingAddressSameAsShippingAddress);
	};

	const isSubmissionAllowed = useMemo(() => {
		if (
			(shippingAddressRef.current.errors && shippingAddressRef.current.errors.length) ||
			(billingAddressRef.current.errors && billingAddressRef.current.errors.some(error => !ALLOWED_BILLING_ADDRESS_VALIDATION_ERROR_CODES.includes(error.code)))
		) {
			return false;
		}
		return true;
	}, [shippingAddressRef.current.errors, billingAddressRef.current.errors]);

	const handleMouseDown = e => {
		setCanDrag(!['A'].includes(e.target.tagName));
	};

	return (
		<div className={'overlayDialog visible'} id="addNewPropertyOverlayDialog" style={{ top: 0 }}>
			<div className="blackoutLayer"></div>
			<div ref={dialogWindowRef} className="overlayDialogWindow" style={{ margin: 0, top: dialogCoordinates.y, left: dialogCoordinates.x }}>
				<a className="closeButton" onClick={handleClose}>
					×
				</a>
				<div onMouseDown={handleMouseDown} style={{ cursor: 'move' }}>
					<h2 className="title">
						{currentScreen === 'FORM'
							? 'Add New Property'
							: currentScreen === 'TAX_VAL'
							? 'Verify Tax ID/Legal Entity Name'
							: currentScreen === 'SHIP_ADDR_VAL'
							? isBillingAddressSameAsShippingAddress
								? 'Verify Shipping/Billing Address'
								: 'Verify Shipping Address'
							: currentScreen === 'BILL_ADDR_VAL'
							? 'Verify Billing Address'
							: currentScreen === 'DONE'
							? acknowledgement && acknowledgement.title
							: ''}
					</h2>
				</div>
				{currentScreen === 'FORM' && (
					<form className="newPropertyForm" onSubmit={handleValidation}>
						<section className="newPropertyFormGrid">
							<div className="newPropertyFormFieldGroup">
								<label htmlFor="emailId">
									<b>Email</b>
								</label>
								<div className="input-wrapper">
									<input
										id="emailId"
										className="newPropertyFormInput"
										type="text"
										name="emailId"
										value={formData.emailId}
										onChange={handleChange}
										onBlur={handleBlur}
										disabled
									/>
									{errors.emailId && (
										<p className="small red my-0">
											<small>{errors.emailId}</small>
										</p>
									)}
								</div>
							</div>
							<div className="newPropertyFormFieldGroup">
								<label htmlFor="taxId">
									<b>Tax ID</b>
								</label>
								<div className="input-wrapper">
									<div style={{ position: 'relative' }}>
										<input
											id="taxId"
											type="text"
											className="newPropertyFormInput"
											name="taxId"
											value={formData.taxId}
											onChange={handleChange}
											onBlur={handleBlur}
										/>
										{isValidatingTaxId && (
											<span
												style={{
													position: 'absolute',
													display: 'inline-block',
													height: '1rem',
													aspectRatio: '1',
													borderRadius: '50%',
													border: '3px solid #aaaaaa',
													borderBottomColor: 'transparent',
													animation: 'spin 1s linear infinite',
													boxSizing: 'border-box',
													right: '.25rem',
													top: '50%',
													translate: '0 -50%'
												}}
											/>
										)}
									</div>
									{errors.taxId && (
										<p className="small red my-0">
											<small>{errors.taxId}</small>
										</p>
									)}
								</div>
							</div>
							<div className="newPropertyFormFieldGroup">
								<label htmlFor="companyName">
									<b>Company Name</b>
								</label>
								<div className="input-wrapper">
									<input
										id="companyName"
										type="text"
										className="newPropertyFormInput"
										name="companyName"
										value={formData.companyName}
										onChange={handleChange}
										onBlur={handleBlur}
									/>
									{errors.companyName && (
										<p className="small red my-0">
											<small>{errors.companyName}</small>
										</p>
									)}
								</div>
							</div>
							<div className="newPropertyFormFieldGroup">
								<label htmlFor="propertyName">
									<b>Property Name</b>
								</label>
								<div className="input-wrapper">
									<input
										id="propertyName"
										type="text"
										className="newPropertyFormInput"
										name="propertyName"
										value={formData.propertyName}
										onChange={handleChange}
										onBlur={handleBlur}
									/>
									{errors.propertyName && (
										<p className="small red my-0">
											<small>{errors.propertyName}</small>
										</p>
									)}
								</div>
							</div>
							<div className="newPropertyFormFieldGroup">
								<label htmlFor="shippingAddress.addressLine1">
									<b>Shipping Address Line 1</b>
								</label>
								<div className="input-wrapper">
									<input
										id="shippingAddress.addressLine1"
										type="text"
										className="newPropertyFormInput"
										name="shippingAddress.addressLine1"
										value={getNestedValue('shippingAddress.addressLine1')}
										onChange={handleChange}
										onBlur={handleBlur}
									/>
									{errors['shippingAddress.addressLine1'] && (
										<p className="small red my-0">
											<small>{errors['shippingAddress.addressLine1']}</small>
										</p>
									)}
								</div>
							</div>
							<div className="newPropertyFormFieldGroup">
								<label htmlFor="shippingAddress.addressLine2">
									<b>Shipping Address Line 2</b>
								</label>
								<div className="input-wrapper">
									<input
										id="shippingAddress.addressLine2"
										type="text"
										className="newPropertyFormInput"
										name="shippingAddress.addressLine2"
										value={getNestedValue('shippingAddress.addressLine2')}
										onChange={handleChange}
										onBlur={handleBlur}
									/>
									{errors['shippingAddress.addressLine2'] && (
										<p className="small red my-0">
											<small>{errors['shippingAddress.addressLine2']}</small>
										</p>
									)}
								</div>
							</div>
							<div className="newPropertyFormFieldGroup">
								<label htmlFor="shippingAddress.city">
									<b>City</b>
								</label>
								<div className="input-wrapper">
									<input
										id="shippingAddress.city"
										type="text"
										className="newPropertyFormInput"
										name="shippingAddress.city"
										value={getNestedValue('shippingAddress.city')}
										onChange={handleChange}
										onBlur={handleBlur}
									/>
									{errors['shippingAddress.city'] && (
										<p className="small red my-0">
											<small>{errors['shippingAddress.city']}</small>
										</p>
									)}
								</div>
							</div>
							<div className="addNewPropertyFormSplitGroup">
								<div className="newPropertyFormSplitItem">
									<label htmlFor="shippingAddress.state">
										<b>State</b>
									</label>
									<div>
										<select
											id="shippingAddress.state"
											className="newPropertyFormSelect"
											name="shippingAddress.state"
											value={getNestedValue('shippingAddress.state')}
											onChange={handleChange}
											onBlur={handleBlur}
										>
											<option value="" disabled>
												Please Select
											</option>
											{US_CONTIGUOUS_STATES.map(state => (
												<option key={state.code} value={state.code}>
													{state.name}
												</option>
											))}
										</select>
										{errors['shippingAddress.state'] && (
											<p className="small red my-0">
												<small>{errors['shippingAddress.state']}</small>
											</p>
										)}
									</div>
								</div>
								<div className="newPropertyFormSplitItem">
									<label htmlFor="shippingAddress.postalCode">
										<b>Zip Code</b>
									</label>
									<div>
										<input
											id="shippingAddress.postalCode"
											type="text"
											className="newPropertyFormInput"
											inputMode="numeric"
											name="shippingAddress.postalCode"
											value={getNestedValue('shippingAddress.postalCode')}
											onChange={handleChange}
											onBlur={handleBlur}
										/>
										{errors['shippingAddress.postalCode'] && (
											<p className="small red my-0">
												<small>{errors['shippingAddress.postalCode']}</small>
											</p>
										)}
									</div>
								</div>
							</div>
							<div className="newPropertyFormFieldGroup">
								<span />
								<label className="input-wrapper small" htmlFor="useSameShipToBillTo">
									<input
										id="useSameShipToBillTo"
										type="checkbox"
										className="align-middle"
										checked={isBillingAddressSameAsShippingAddress}
										onChange={toggleIsBillingAddressSameAsShippingAddress}
									/>
									<b className="ml-1 align-middle font-medium">Same As Shipping Address</b>
								</label>
							</div>
							<div className="newPropertyFormFieldGroup">
								<label htmlFor="billingAddress.addressLine1">
									<b>Billing Address Line 1</b>
								</label>
								<div className="input-wrapper">
									<input
										id="billingAddress.addressLine1"
										type="text"
										className="newPropertyFormInput"
										name="billingAddress.addressLine1"
										value={getNestedValue('billingAddress.addressLine1')}
										onChange={handleChange}
										onBlur={handleBlur}
										disabled={isBillingAddressSameAsShippingAddress}
									/>
									{errors['billingAddress.addressLine1'] && (
										<p className="small red my-0">
											<small>{errors['billingAddress.addressLine1']}</small>
										</p>
									)}
								</div>
							</div>
							<div className="newPropertyFormFieldGroup">
								<label htmlFor="billingAddress.addressLine2">
									<b>Billing Address Line 2</b>
								</label>
								<div className="input-wrapper">
									<input
										id="billingAddress.addressLine2"
										type="text"
										className="newPropertyFormInput"
										name="billingAddress.addressLine2"
										value={getNestedValue('billingAddress.addressLine2')}
										onChange={handleChange}
										onBlur={handleBlur}
										disabled={isBillingAddressSameAsShippingAddress}
									/>
									{errors['billingAddress.addressLine2'] && (
										<p className="small red my-0">
											<small>{errors['billingAddress.addressLine2']}</small>
										</p>
									)}
								</div>
							</div>
							<div className="newPropertyFormFieldGroup">
								<label htmlFor="billingAddress.city">
									<b>City</b>
								</label>
								<div className="input-wrapper">
									<input
										id="billingAddress.city"
										type="text"
										className="newPropertyFormInput"
										name="billingAddress.city"
										value={getNestedValue('billingAddress.city')}
										onChange={handleChange}
										onBlur={handleBlur}
										disabled={isBillingAddressSameAsShippingAddress}
									/>
									{errors['billingAddress.city'] && (
										<p className="small red my-0">
											<small>{errors['billingAddress.city']}</small>
										</p>
									)}
								</div>
							</div>
							<div className="addNewPropertyFormSplitGroup">
								<div className="newPropertyFormSplitItem">
									<label htmlFor="billingAddress.state">
										<b>State</b>
									</label>
									<div>
										<select
											id="billingAddress.state"
											className="newPropertyFormSelect"
											name="billingAddress.state"
											value={getNestedValue('billingAddress.state')}
											onChange={handleChange}
											onBlur={handleBlur}
											disabled={isBillingAddressSameAsShippingAddress}
										>
											<option value="" disabled>
												Please Select
											</option>
											{US_CONTIGUOUS_STATES.map(state => (
												<option key={state.code} value={state.code}>
													{state.name}
												</option>
											))}
										</select>
										{errors['billingAddress.state'] && (
											<p className="small red my-0">
												<small>{errors['billingAddress.state']}</small>
											</p>
										)}
									</div>
								</div>
								<div className="newPropertyFormSplitItem">
									<label htmlFor="billingAddress.postalCode">
										<b>Zip Code</b>
									</label>
									<div>
										<input
											id="billingAddress.postalCode"
											type="text"
											className="newPropertyFormInput"
											inputMode="numeric"
											name="billingAddress.postalCode"
											value={getNestedValue('billingAddress.postalCode')}
											onChange={handleChange}
											onBlur={handleBlur}
											disabled={isBillingAddressSameAsShippingAddress}
										/>
										{errors['billingAddress.postalCode'] && (
											<p className="small red my-0">
												<small>{errors['billingAddress.postalCode']}</small>
											</p>
										)}
									</div>
								</div>
							</div>
							<div className="newPropertyFormFieldGroup">
								<label htmlFor="phoneNumber">
									<b>Phone</b>
								</label>
								<div className="input-wrapper">
									<input
										id="phoneNumber"
										type="text"
										className="newPropertyFormInput"
										name="phoneNumber"
										value={formData.phoneNumber}
										onChange={handleChange}
										onBlur={handleBlur}
									/>
									{errors.phoneNumber && (
										<p className="small red my-0">
											<small>{errors.phoneNumber}</small>
										</p>
									)}
								</div>
							</div>
						</section>
						<p className="newPropertyFormSubmitContainer">
							<button type="submit" className="newPropertyFormSubmitButton" disabled={isValidatingTaxId}>
								Submit
							</button>
						</p>
					</form>
				)}
				{currentScreen === 'TAX_VAL' && (
					<div>
						<p>Please verify the Tax ID and Legal business name exactly as filed with the IRS</p>
						<div className="actions flex justify-end">
							<button onClick={handleCloseTaxIdVerificationModal}>OK</button>
						</div>
					</div>
				)}
				{currentScreen === 'SHIP_ADDR_VAL' && (
					<div>
						<AddressVerificationForm
							onAddressSelection={e => {
								shippingAddressRef.current.selected = e.target.value;
							}}
							enteredAddress={shippingAddressRef.current.entered}
							suggestedAddress={shippingAddressRef.current.validated}
							isEnteredAddressCorrect={false}
							disabledAddressTypes={
								(shippingAddressRef.current.errors && shippingAddressRef.current.errors.length) ||
								(shippingAddressRef.current.entered && isPoOrHcOrPmbAddress(shippingAddressRef.current.entered))
									? ['asEntered', 'asCorrected']
									: ['asEntered']
							}
							errors={shippingAddressRef.current.errors ? shippingAddressRef.current.errors.map(error => error.message) : []}
							noActions
						/>
						<section className="text-center">
							{shippingAddressRef.current.errorMessage && <p className="red">{shippingAddressRef.current.errorMessage}</p>}
							{shippingAddressRef.current.messages &&
								shippingAddressRef.current.messages.map(message => (
									<p key={message} className="my-0">
										{message}
									</p>
								))}
						</section>
						<div className="actions flex justify-end">
							<button className="orangeButton" onClick={() => setCurrentScreen('FORM')}>
								{shippingAddressRef.current.cancelLabel}
							</button>
							<button
								onClick={handleConfirmShippingAddress}
								disabled={
									!taxIdValidationRef.current.isError &&
									(isBillingAddressSameAsShippingAddress
										? !isSubmissionAllowed || (shippingAddressRef.current.entered && isPoOrHcOrPmbAddress(shippingAddressRef.current.entered))
										: false)
								}
							>
								{shippingAddressRef.current.confirmLabel}
							</button>
						</div>
					</div>
				)}
				{currentScreen === 'BILL_ADDR_VAL' && (
					<div>
						<AddressVerificationForm
							onAddressSelection={e => {
								billingAddressRef.current.selected = e.target.value;
							}}
							enteredAddress={billingAddressRef.current.entered}
							suggestedAddress={billingAddressRef.current.validated}
							isEnteredAddressCorrect={false}
							disabledAddressTypes={billingAddressRef.current.errors && billingAddressRef.current.errors.length ? ['asEntered', 'asCorrected'] : ['asEntered']}
							noActions
						/>
						<section className="text-center">
							{billingAddressRef.current.errorMessage && <p className="red">{billingAddressRef.current.errorMessage}</p>}
							{billingAddressRef.current.messages &&
								billingAddressRef.current.messages.map(message => (
									<p key={message} className="my-0">
										{message}
									</p>
								))}
						</section>
						<div className="actions flex justify-end">
							<button className="orangeButton" onClick={handleShippingAddressVerificationModal}>
								{billingAddressRef.current.cancelLabel}
							</button>
							<button
								onClick={handleConfirmBillingAddress}
								disabled={
									!taxIdValidationRef.current.isError &&
									(!isSubmissionAllowed || (shippingAddressRef.current.entered && isPoOrHcOrPmbAddress(shippingAddressRef.current.entered)))
								}
							>
								{billingAddressRef.current.confirmLabel}
							</button>
						</div>
					</div>
				)}
				{currentScreen === 'DONE' && (
					<div>
						{acknowledgement.content}
						<div className="actions flex justify-end">
							<button onClick={handleClose}>OK</button>
						</div>
					</div>
				)}
			</div>
		</div>
	);
};

export default AddNewPropertyDialog;



// WEBPACK FOOTER //
// ./src/App/components/dialogs/addNewPropertyDialog.jsx